/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { uuid } from '@src/app/model/abstract-object';
import { ForumRole } from '@src/app/model/forum';
import { IsNarrowService } from '@src/app/shared/is-narrow.service';
import { PillColorDef, PillColors } from '@src/app/shared/form/form-pill/form-pill.component';
import { channelColour, channelIcon } from '@src/app/modules/social/channelPillFields';
import { NgClass } from '@angular/common';

class VisibleChannel {
    name: string;
    id: uuid;
    from: string;
    roles: string[];
    paint: PillColorDef;
    pillColor: string;
    icon: string;
}

@Component({
    selector: 'app-channel-picker',
    imports: [MatTooltipModule, MatChipsModule, MatIconModule, NgClass],
    templateUrl: './channel-picker.component.html',
    styleUrl: './channel-picker.component.scss'
})
export class ChannelPickerComponent implements OnChanges {

    cus = inject(CurrentUserService);
    screenSvc = inject(IsNarrowService);

    @Input() currentChannel: uuid;
    @Input() contentType: string;
    @Output() channelSelected = new EventEmitter<uuid>()

    channels: VisibleChannel[] = []
    selectedChannel: VisibleChannel;

    constructor() {
        this.cus.getCurrentUser().pipe(takeUntilDestroyed()).subscribe( u => {
            u.forumRoles.forEach( fr => {
                const cv = this.getChannel(fr.forumId);
                if (cv) {
                    cv.from += this.describePrivilege(fr);
                    cv.roles.push(fr.roleName);

                } else {
                    const paint = PillColors[channelColour(fr.pillColor)];
                    this.channels.push({ name: this.getName(fr), id: fr.forumId, roles: [fr.roleName],
                        from: this.describePrivilege(fr), paint, pillColor: fr.pillColor, icon: channelIcon(fr.pillIcon)});
                }
            })
        });
    }

    private getChannel(id: uuid) {
        return this.channels.find( c => c.id === id);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.currentChannel) {
            if (changes.currentChannel.currentValue) {
                this.selectedChannel = this.channels.find( fr => fr.id === changes.currentChannel.currentValue);
            } else {
                this.selectedChannel = null;
            }
        }
    }

    selectChannel(c) {
        if (c.id === this.selectedChannel?.id) {
            this.channelSelected.emit(null);
        } else {
            this.channelSelected.emit(c?.id);
        }
    }

    describePrivilege(fr: ForumRole) {
        const privs = [];
        if (fr.canView) privs.push('view')
        if (fr.canCreate) privs.push('create')
        if (fr.canEdit) privs.push('edit')
        if (fr.canComment) privs.push('comment')
        if (fr.canVote) privs.push('vote')
        if (fr.canVote) privs.push('receive notifications');
        const last = privs.pop();
        const msg = $localize `Click to add/remove filter for  ${fr.forumName} items. `;
        if (privs.length === 1) {
            return msg + 'You can ' + last + ' because you have ' + fr.roleName + ' role. ';
        } else {
            return msg + 'You can ' + privs.join(', ') + ' and ' + last + ' because you have ' + fr.roleName + ' role. ';
        }
    }

    getName(c: ForumRole) : string {
        if (this.screenSvc.isPhone()) { // Tacky - but cleans up display nicely...
            return c.forumName.replace('Forum', '')
                .replace('forum', '')
                .replace('Channel', '')
                .replace('channel', '').trim();
        }
        return c.forumName;
    }
}
